import React from 'react';
import { Box } from '@mui/material';
import type { IconType } from 'react-icons';
import {
    FaInstagram,
    FaYoutube,
    FaFacebookF,
    FaXTwitter,
    FaTiktok,
    FaThreads,
    FaLinkedinIn,
    FaWhatsapp,
    FaSnapchat,
    FaPinterestP,
} from 'react-icons/fa6';

export type SocialPlatform =
    | 'instagram'
    | 'youtube'
    | 'facebook'
    | 'x'
    | 'tiktok'
    | 'threads'
    | 'linkedin'
    | 'whatsapp'
    | 'snapchat'
    | 'pinterest';

// Each logo is drawn in white on a brand-coloured circle; Instagram gets its
// gradient. Mirrors SocialIconStack in clanconnect_mobile.
const PLATFORMS: Record<SocialPlatform, { Icon: IconType; bg: string; fg?: string }> = {
    instagram: { Icon: FaInstagram, bg: 'linear-gradient(45deg, #FEDA75, #FA7E1E, #D62976, #962FBF, #4F5BD5)' },
    youtube: { Icon: FaYoutube, bg: '#FF0000' },
    facebook: { Icon: FaFacebookF, bg: '#1877F2' },
    x: { Icon: FaXTwitter, bg: '#000000' },
    tiktok: { Icon: FaTiktok, bg: '#000000' },
    threads: { Icon: FaThreads, bg: '#000000' },
    linkedin: { Icon: FaLinkedinIn, bg: '#0A66C2' },
    whatsapp: { Icon: FaWhatsapp, bg: '#25D366' },
    snapchat: { Icon: FaSnapchat, bg: '#FFFC00', fg: '#000000' },
    pinterest: { Icon: FaPinterestP, bg: '#E60023' },
};

interface SocialIconStackProps {
    platforms: SocialPlatform[];
    // Text pill shown after the icons (e.g. "+ Anywhere").
    extraLabel?: string;
    size?: number;
}

export const SocialIconStack: React.FC<SocialIconStackProps> = ({ platforms, extraLabel, size = 18 }) => {
    const overlap = platforms.length > 1 ? Math.round(size * 0.3) : 0;

    return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
            {platforms.map((platform, i) => {
                const { Icon, bg, fg = '#ffffff' } = PLATFORMS[platform];
                return (
                    <Box
                        key={platform}
                        title={platform}
                        sx={{
                            width: size, height: size, borderRadius: '50%',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            background: bg, color: fg,
                            border: '1.5px solid #ffffff', boxSizing: 'border-box',
                            ml: i === 0 ? 0 : `-${overlap}px`,
                            zIndex: platforms.length - i,
                        }}
                    >
                        <Icon size={Math.round(size * 0.55)} />
                    </Box>
                );
            })}
            {extraLabel && (
                <Box
                    sx={{
                        height: size, borderRadius: size / 2, px: 1, ml: 0.5,
                        display: 'flex', alignItems: 'center',
                        bgcolor: '#f1f5f9', color: '#64748b',
                        fontSize: Math.round(size * 0.46), fontWeight: 600, whiteSpace: 'nowrap',
                    }}
                >
                    {extraLabel}
                </Box>
            )}
        </Box>
    );
};

export default SocialIconStack;
